import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store/store';
import { API_ENDPOINTS } from '../api/endpoints';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';

const POLL_INTERVAL_MS = 2_000;
// The worker matches up to BULK_SEARCH_MAX_ROWS (500) names sequentially against the DB and can run
// for the full Lambda timeout (900s). Poll for at least that long — 600 x 2s = 20 min — so a large
// upload that is still legitimately matching is never abandoned mid-run. TD-2660: the old 180 / 6min
// ceiling reverted the UI to the upload form while the worker was still running, and the subsequent
// re-upload then 409'd ("a bulk company search is already running"). On overrun we surface 'timeout'
// (the job may still be running; the BE self-heals a stranded row to 'failed' at its staleness
// bound) and KEEP the searchId so the user can resume() instead of losing the run.
const POLL_MAX_ATTEMPTS = 600;
// A single transient poll error (gateway blip / brief 5xx) should not fail the whole run; retry a
// few times with a short linear backoff before giving up. Mirrors useEnrichmentJobPolling.
const MAX_ERROR_RETRIES = 3;
const ERROR_RETRY_BASE_MS = 1_000;

const DEFAULT_ERROR =
  "We couldn't process that file. Please check it matches the template and try again.";
const TIMEOUT_ERROR =
  'This is taking longer than expected — your file may still be processing. Please try again in a moment.';

// A FileReader data URL is "data:<mime>;base64,<payload>"; the BE wants just the payload
// (Buffer.from(fileBase64, 'base64')).
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL always yields a string; guard the type explicitly rather than String()-
      // coercing (which would stringify a non-string result to "[object …]").
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });

/**
 * Drives the bulk company upload search (TD-2344): base64-upload the filled .xlsx template
 * (POST /bulk-company-search), then poll POST /poll-bulk-company-search every 2s until the match
 * worker reaches a terminal state. The poll endpoint's dataMapper already maps candidates to the
 * CompanyNameResultItem shape, so `results` is render-ready.
 *
 * Single-instance by design (mount once): api.js's cancelRequest registry is path-global, so the
 * poll must not be duplicated across mounts. ProfilerSearch is the only consumer.
 *
 * @returns {{
 *   start: (file: File) => void,   // upload + begin polling
 *   reset: () => void,             // back to idle (new upload / go back)
 *   resume: () => void,            // re-poll the held searchId after a soft 'timeout' (TD-2660)
 *   status: 'idle'|'uploading'|'pending'|'completed'|'failed'|'timeout',
 *   results: object|null,          // completed poll payload { searchId, counts, rows }
 *   searchId: string|null,
 *   error: string,
 *   progress: {processed: number, total: number}|null  // live match counter (TD-2441)
 * }}
 */
const useBulkCompanySearch = () => {
  const store = useStore();
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const [error, setError] = useState('');
  // Live match progress { processed, total } for the "Matching companies… X / N" counter
  // (TD-2441). null until the trigger's namesToMatch (total) lands; processed climbs from the poll.
  const [progress, setProgress] = useState(null);

  // Refs let the poll loop read the latest search id / counters without re-creating the callback
  // on every state change (which would race the timer and double-fire requests).
  const isMounted = useRef(true);
  const attemptsRef = useRef(0);
  const errorRetriesRef = useRef(0);
  const timerRef = useRef(null);
  const searchIdRef = useRef(null);
  // The total (deduped namesToMatch) from the 202 trigger response; lets the counter show "0 / N"
  // before the worker's first progress write, and is the fallback total until the poll carries one.
  const totalRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // TD-2382: a terminal failure records the error (for callers/tests) and raises the standard
  // top-of-screen system message, so no consumer renders the error as inline text.
  // TD-2660: a soft 'timeout' is the exception — it has its own inline "keep checking" affordance
  // (ProfilerSearch), so it must NOT also raise the banner, whose "try again in a moment" copy
  // contradicts the panel's "you don't need to upload again".
  const surfaceError = useCallback(
    (message, nextStatus = 'failed') => {
      setError(message);
      setStatus(nextStatus);
      if (nextStatus !== 'timeout') {
        store.utilsStore?.setHeaderMessage?.(message, HEADER_MESSAGE_TYPES.ERROR);
      }
    },
    [store]
  );

  const poll = useCallback(() => {
    if (!isMounted.current || !searchIdRef.current) return;
    attemptsRef.current += 1;
    store.makeRequest({
      endpoint: API_ENDPOINTS.POLL_BULK_COMPANY_SEARCH,
      body: { searchId: searchIdRef.current },
      onSuccess: (data) => {
        if (!isMounted.current) return;
        errorRetriesRef.current = 0; // a good poll clears the transient-error budget
        const next = data?.status;
        if (next === 'completed') {
          setResults(data);
          setStatus('completed');
          return;
        }
        if (next === 'failed') {
          surfaceError(DEFAULT_ERROR);
          return;
        }
        // Live progress (TD-2441): the poll carries { processed, total } once the worker starts
        // writing it; fall back to the 202 total so the counter shows a total even before then.
        const polledTotal = Number(data?.progress?.total);
        const total = Number.isFinite(polledTotal) && polledTotal > 0 ? polledTotal : totalRef.current;
        if (total) {
          const polledProcessed = Number(data?.progress?.processed);
          setProgress((prev) => {
            const prevProcessed = prev?.processed ?? 0;
            // Never move the counter backwards (poll retries / out-of-order reads).
            const nextProcessed = Number.isFinite(polledProcessed)
              ? Math.max(prevProcessed, polledProcessed)
              : prevProcessed;
            return { processed: Math.min(nextProcessed, total), total };
          });
        }
        // still pending -> schedule the next tick unless we've hit the ceiling
        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
          surfaceError(TIMEOUT_ERROR, 'timeout');
          return;
        }
        setStatus('pending');
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      },
      onError: (message, response) => {
        if (!isMounted.current) return;
        errorRetriesRef.current += 1;
        if (errorRetriesRef.current <= MAX_ERROR_RETRIES) {
          timerRef.current = setTimeout(poll, ERROR_RETRY_BASE_MS * errorRetriesRef.current);
          return;
        }
        // A 4xx is the one poll error that IS a definitive answer about this searchId (unknown id,
        // not ours), so it stays terminal. Without this it would fall through to 'timeout', which
        // both offers a "keep checking" button that can never succeed and — since 'timeout' also
        // suppresses the header banner — surfaces no error anywhere at all.
        const httpStatus = Number(response?.httpStatus);
        if (httpStatus >= 400 && httpStatus < 500) {
          surfaceError(message || response?.errorMessage || DEFAULT_ERROR);
          return;
        }
        // TD-2660: any other transport failure (gateway blip / sustained 5xx / network drop /
        // client-side timeout, which carries no httpStatus) — unlike a poll body of
        // status 'failed' — tells us nothing about the worker, which is very likely still matching.
        // Treat exhausted retries as the same recoverable 'timeout' as the attempt ceiling (keeps
        // the searchId + the resume affordance) rather than a terminal 'failed' that dead-ends into
        // a 409 on re-upload — the exact TD-2660 symptom, just reached down a different path.
        surfaceError(TIMEOUT_ERROR, 'timeout');
      }
    });
  }, [store, surfaceError]);

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    attemptsRef.current = 0;
    errorRetriesRef.current = 0;
    searchIdRef.current = null;
    totalRef.current = null;
    setStatus('idle');
    setResults(null);
    setSearchId(null);
    setError('');
    setProgress(null);
  }, []);

  // TD-2660: resume polling an existing run without re-uploading. After a soft 'timeout' (or a
  // transient give-up) the searchId is still held; a completed run has already auto-finalized its
  // results on the BE, so continuing to poll surfaces them. No-op when there is nothing to resume.
  const resume = useCallback(() => {
    if (!searchIdRef.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    attemptsRef.current = 0;
    errorRetriesRef.current = 0;
    setError('');
    setStatus('pending');
    poll();
  }, [poll]);

  const start = useCallback(
    async (file) => {
      if (!file) return;
      reset();
      setStatus('uploading');

      let fileBase64;
      try {
        fileBase64 = await fileToBase64(file);
      } catch {
        if (!isMounted.current) return;
        surfaceError(DEFAULT_ERROR);
        return;
      }
      if (!isMounted.current) return;

      store.makeRequest({
        endpoint: API_ENDPOINTS.BULK_COMPANY_SEARCH,
        body: { fileBase64, fileName: file.name },
        onSuccess: (data) => {
          if (!isMounted.current) return;
          if (!data?.searchId) {
            surfaceError(DEFAULT_ERROR);
            return;
          }
          searchIdRef.current = data.searchId;
          attemptsRef.current = 0;
          errorRetriesRef.current = 0;
          // Seed the counter from the 202 total (deduped namesToMatch) so it shows "0 / N"
          // immediately, before the first poll (TD-2441).
          const total = Number(data.namesToMatch);
          totalRef.current = Number.isFinite(total) && total > 0 ? total : null;
          setProgress(totalRef.current ? { processed: 0, total: totalRef.current } : null);
          setSearchId(data.searchId);
          setStatus('pending');
          poll();
        },
        onError: (message, response) => {
          if (!isMounted.current) return;
          // 400/403/409 carry response.errorMessage; authz errors carry the joined `message`.
          surfaceError(message || response?.errorMessage || DEFAULT_ERROR);
        }
      });
    },
    [store, reset, poll, surfaceError]
  );

  return { start, reset, resume, status, results, searchId, error, progress };
};

export default useBulkCompanySearch;
